import type { ReactNode } from 'react';
import './SettingsLayout.css';

export interface SettingsTabDef {
  id: string;
  label: string;
  icon?: ReactNode;
}

interface SettingsLayoutProps {
  title: string;
  tabs: SettingsTabDef[];
  activeTab: string;
  onTabChange: (id: string) => void;
  children: ReactNode;
  /** Persistent action(s) pinned to the bottom of the nav rail (e.g. Logout) —
   *  always visible, not inside any tab. */
  navFooter?: ReactNode;
}

/**
 * Settings shell with a left vertical nav rail on desktop that collapses to a
 * horizontal scrollable tab bar on mobile. Section content is supplied by the
 * caller (typically `tabs.find(...) === activeTab ? <Section /> : null`).
 */
export function SettingsLayout({ title, tabs, activeTab, onTabChange, children, navFooter }: SettingsLayoutProps) {
  return (
    <div className="settings-layout">
      <header className="settings-layout-header">
        <h1 className="settings-layout-title">{title}</h1>
      </header>

      <div className="settings-layout-body">
        <nav className="settings-nav" aria-label="Settings sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => onTabChange(tab.id)}
              aria-current={activeTab === tab.id ? 'page' : undefined}
            >
              {tab.icon && <span className="settings-nav-icon">{tab.icon}</span>}
              <span className="settings-nav-label">{tab.label}</span>
            </button>
          ))}
          {navFooter && <div className="settings-nav-footer">{navFooter}</div>}
        </nav>

        <div className="settings-content">{children}</div>
      </div>
    </div>
  );
}
